import { ChangeEvent, useState } from 'react'

import { useUser } from 'app/User'
import { useTrayManagementContext } from 'views/TrayManagementLayout/TrayManagement/TrayManagement.context'

import { Filter, SubFilter } from './TrayManagementFilters.types'
import { TrayType } from '../AddTraySelectType/AddTraySelectType.types'
import { Option } from 'common/types'
import { TrayStatus } from 'components/organisms/EditTrayModal/EditTrayModal.types'
import { useNavigate } from 'react-router-dom'
import { extractTrayJSONData } from 'lib/utils/extractTrayJSONData'
import { DateRangeFilter } from 'components/organisms/FilterDrawer/FilterDrawer.types'

const GENERAL_SUBFILTERS: SubFilter[] = [
  { title: 'Ready for Pickup', filterValue: 'readyForPickup' },
  { title: 'Approved', filterValue: 'approved' },
  { title: 'Rejected', filterValue: 'rejected' },
  { title: 'Dropped Off', filterValue: 'droppedOff' },
  { title: 'Ready to Fill', filterValue: 'readyToFill' },
  { title: 'Assigned', filterValue: 'assigned' },
  { title: 'Unassigned', filterValue: 'unAssigned' },
  { title: 'Case Completed/ Dirty', filterValue: 'caseComplete' },
]

const HOSPITAL_SUBFILTERS: SubFilter[] = [
  { title: 'Ready to Fill', filterValue: 'readyToFill' },
  { title: 'Assigned', filterValue: 'assigned' },
  { title: 'Unassigned', filterValue: 'unAssigned' },
]

const NURSE_FILTERS: Filter[] = [
  {
    title: 'Hospital Trays',
    filterValue: 'facility',
    subFilters: HOSPITAL_SUBFILTERS,
  },
]

const useTrayManagementFiltersLogic = () => {
  const navigate = useNavigate()

  const { isNurse } = useUser()
  const {
    setSelectedFilters,
    selectedFilters,
    setSelectedCategory,
    selectedCategory,
    trayIdFilterValue,
    setTrayIdFilterValue,
    companyFilterValue,
    setCompanyFilterValue,
    trays,
    surgeonFilterValue,
    setSurgeonFilterValue,
    patientFilterValue,
    setPatientFilterValue,
    roomFilterValue,
    setRoomFilterValue,
    dateFilterValue,
    setDateFilterValue,
    creationDateFilterValue,
    setCreationDateFilterValue,
  } = useTrayManagementContext()

  const vendorOptions: Option[] = Array.from(
    new Set(
      trays.filter((tray) => tray.companyName).map((tray) => tray.companyName)
    )
  )
    .filter((companyName): companyName is string => companyName !== undefined)
    .map((companyName) => ({
      id: companyName,
      name: companyName,
    }))

  const listOfFilters: Filter[] = [
    { title: 'All Trays', filterValue: '', subFilters: GENERAL_SUBFILTERS },
    ...(isNurse ? NURSE_FILTERS : []),
    {
      title: 'Loaner Trays',
      filterValue: 'loaner',
      subFilters: GENERAL_SUBFILTERS,
    },
    {
      title: 'Consignment Trays',
      filterValue: 'consigned',
      subFilters: GENERAL_SUBFILTERS,
    },
  ]

  /**
   * Handle filter click event
   * @param filter - The filter value to set
   */
  const handleFilterClick = (filter: Filter): void => {
    const isSameCategory = filter.filterValue === selectedCategory

    setSelectedCategory(filter.filterValue as TrayType['category'])

    const subFilterValues = filter.subFilters?.map((sf) => sf.filterValue) || []
    const allSubFiltersSelected = subFilterValues.every((sf) =>
      selectedFilters.has(sf)
    )

    if (allSubFiltersSelected && isSameCategory) {
      setSelectedFilters(new Set())
    } else {
      setSelectedFilters(new Set(subFilterValues))
    }
  }

  /**
   * Handle subfilter click event
   * @param subFilterValue - The value of the subfilter
   */
  const handleSubFilterClick = (
    subFilterValue: SubFilter['filterValue']
  ): void => {
    const newSelectedFilters = new Set(selectedFilters)
    if (newSelectedFilters.has(subFilterValue)) {
      newSelectedFilters.delete(subFilterValue)
    } else {
      newSelectedFilters.add(subFilterValue)
    }
    setSelectedFilters(newSelectedFilters)
  }

  /**
   * Handle tray ID filter change event
   * @param e - The change event from the input
   */
  const handleTrayIdFilterChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setTrayIdFilterValue(e.target.value)
  }

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)

  const toggleFilterDrawer = () => {
    setIsFilterDrawerOpen((prev) => !prev)
  }

  const getActiveFilters = () => {
    const filters: Array<{ id: string; label: string }> = []

    if (companyFilterValue) {
      filters.push({
        id: 'vendor',
        label: `Vendor: ${companyFilterValue.name}`,
      })
    }

    if (selectedCategory) {
      const categoryFilter = listOfFilters.find(
        (f) => f.filterValue === selectedCategory
      )
      if (categoryFilter) {
        filters.push({
          id: 'category',
          label: `Category: ${categoryFilter.title}`,
        })
      }
    }

    selectedFilters.forEach((filter) => {
      const subFilter = GENERAL_SUBFILTERS.find(
        (sf) => sf.filterValue === filter
      )
      if (subFilter && filter) {
        filters.push({
          id: filter,
          label: `Status: ${subFilter.title}`,
        })
      }
    })

    // Add the new case filter checks
    if (surgeonFilterValue) {
      filters.push({
        id: 'surgeon',
        label: `Surgeon: ${surgeonFilterValue}`,
      })
    }

    if (patientFilterValue) {
      filters.push({
        id: 'patient',
        label: `Patient: ${patientFilterValue}`,
      })
    }

    if (roomFilterValue) {
      filters.push({
        id: 'room',
        label: `Room: ${roomFilterValue}`,
      })
    }

    // Format date range for display
    if (dateFilterValue.startDate || dateFilterValue.endDate) {
      let dateLabel = 'Date: '
      if (dateFilterValue.startDate && dateFilterValue.endDate) {
        dateLabel += `${dateFilterValue.startDate} - ${dateFilterValue.endDate}`
      } else if (dateFilterValue.startDate) {
        dateLabel += `From ${dateFilterValue.startDate}`
      } else if (dateFilterValue.endDate) {
        dateLabel += `Until ${dateFilterValue.endDate}`
      }

      filters.push({
        id: 'date',
        label: dateLabel,
      })
    }

    // Format creation date range for display
    if (creationDateFilterValue.startDate || creationDateFilterValue.endDate) {
      let dateLabel = 'Drop-off: '
      if (
        creationDateFilterValue.startDate &&
        creationDateFilterValue.endDate
      ) {
        dateLabel += `${creationDateFilterValue.startDate} - ${creationDateFilterValue.endDate}`
      } else if (creationDateFilterValue.startDate) {
        dateLabel += `From ${creationDateFilterValue.startDate}`
      } else if (creationDateFilterValue.endDate) {
        dateLabel += `Until ${creationDateFilterValue.endDate}`
      }

      filters.push({
        id: 'creationDate',
        label: dateLabel,
      })
    }

    return filters
  }

  const handleRemoveFilter = (filterId: string) => {
    if (filterId === 'vendor') {
      setCompanyFilterValue(undefined)
    } else if (filterId === 'category') {
      setSelectedCategory(undefined)
    } else if (filterId === 'surgeon') {
      setSurgeonFilterValue('')
    } else if (filterId === 'patient') {
      setPatientFilterValue('')
    } else if (filterId === 'room') {
      setRoomFilterValue('')
    } else if (filterId === 'date') {
      setDateFilterValue({ startDate: null, endDate: null })
    } else if (filterId === 'creationDate') {
      setCreationDateFilterValue({ startDate: null, endDate: null })
    } else {
      const newFilters = new Set(selectedFilters)
      newFilters.delete(filterId as TrayStatus)
      setSelectedFilters(newFilters)
    }
  }

  const handleAddTrayClick = () => {
    navigate('/tray-management/add-tray')
  }

  // Extract unique surgeons, rooms from trays for autocomplete
  const surgeonOptions: Option[] = Array.from(
    new Set(
      trays
        .map((tray) => {
          const trayDetails = extractTrayJSONData(tray)
          const surgery = trayDetails?.surgeryDetails
          if (surgery?.visit?.attendingProvider) {
            const lastName = surgery.visit.attendingProvider.lastName || ''
            const firstNameInitial =
              surgery.visit.attendingProvider.firstName?.charAt(0) || ''
            return `${lastName}, ${firstNameInitial}`
          }
          return trayDetails?.selectedSurgeon
        })
        .filter(Boolean)
    )
  ).map((name) => ({
    id: name as string,
    name: name as string,
  }))

  const roomOptions: Option[] = Array.from(
    new Set(
      trays
        .map((tray) => {
          const trayDetails = extractTrayJSONData(tray)
          return trayDetails?.surgeryDetails?.visit?.location?.room
        })
        .filter(Boolean)
    )
  ).map((room) => ({
    id: room as string,
    name: room as string,
  }))

  // Handle case filter changes
  const handleSurgeonFilterChange = (
    e: ChangeEvent<HTMLInputElement>
  ): void => {
    setSurgeonFilterValue(e.target.value)
  }

  const handlePatientFilterChange = (
    e: ChangeEvent<HTMLInputElement>
  ): void => {
    setPatientFilterValue(e.target.value)
  }

  const handleRoomFilterChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setRoomFilterValue(e.target.value)
  }

  // Handle date range filter changes
  const handleDateFilterChange = (range: DateRangeFilter): void => {
    setDateFilterValue(range)
  }

  const handleCreationDateFilterChange = (range: DateRangeFilter): void => {
    setCreationDateFilterValue(range)
  }

  // Clear all filters including the date range filters
  const handleClearAllFilters = () => {
    setSelectedFilters(new Set())
    setSelectedCategory(undefined)
    setTrayIdFilterValue('')
    setCompanyFilterValue(undefined)
    setSurgeonFilterValue('')
    setPatientFilterValue('')
    setRoomFilterValue('')
    setDateFilterValue({ startDate: null, endDate: null })
    setCreationDateFilterValue({ startDate: null, endDate: null })
  }

  return {
    listOfFilters,
    handleFilterClick,
    handleSubFilterClick,
    selectedFilters,
    selectedCategory,
    trayIdFilterValue,
    handleTrayIdFilterChange,
    vendorOptions,
    companyFilterValue,
    setCompanyFilterValue,
    handleClearAllFilters,
    isFilterDrawerOpen,
    toggleFilterDrawer,
    getActiveFilters,
    handleRemoveFilter,
    handleAddTrayClick,
    surgeonFilterValue,
    patientFilterValue,
    roomFilterValue,
    dateFilterValue,
    handleSurgeonFilterChange,
    handlePatientFilterChange,
    handleRoomFilterChange,
    handleDateFilterChange,
    surgeonOptions,
    roomOptions,
    creationDateFilterValue,
    handleCreationDateFilterChange,
  }
}

export default useTrayManagementFiltersLogic
